/**
 * <s-reaction-reel> — Row of short reaction clips.
 *
 * - Each <video data-src> loads only when it scrolls near the viewport.
 * - Visible clips play muted on loop; off-screen clips pause.
 * - With prefers-reduced-motion, nothing autoplays; the sound button starts playback instead.
 * - button[data-sound] toggles sound for its clip; only one clip is ever unmuted.
 */
class SReactionReel extends HTMLElement {
  #observer;
  #reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    this.#observer = new IntersectionObserver((entries) => this.#onIntersect(entries), {
      rootMargin: '200px',
      threshold: 0.4,
    });
    this.querySelectorAll('video[data-src]').forEach((video) => this.#observer.observe(video));
    this.addEventListener('click', (event) => this.#onClick(event));
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }

  #load(video) {
    if (video.dataset.loaded) return;
    video.src = video.dataset.src;
    // Optional speed-up for time-lapses: <video data-rate="3">.
    const rate = parseFloat(video.dataset.rate);
    if (rate > 0) {
      video.defaultPlaybackRate = rate;
      video.playbackRate = rate;
      video.addEventListener('loadedmetadata', () => { video.playbackRate = rate; }, { once: true });
    }
    video.preload = 'metadata';
    video.dataset.loaded = 'true';
  }

  #onIntersect(entries) {
    for (const { target: video, isIntersecting } of entries) {
      if (isIntersecting) {
        this.#load(video);
        if (!this.#reduceMotion.matches) video.play().catch(() => {});
      } else {
        video.pause();
        if (!video.muted) this.#setSound(video, false);
      }
    }
  }

  #setSound(video, on) {
    video.muted = !on;
    const button = video.parentElement.querySelector('[data-sound]');
    button?.setAttribute('aria-pressed', String(on));
  }

  #onClick(event) {
    const button = event.target.closest('[data-sound]');
    if (!button) return;
    const video = button.parentElement.querySelector('video');
    if (!video) return;
    this.#load(video);
    const turnOn = video.muted;
    // Only one clip talks at a time.
    this.querySelectorAll('video').forEach((other) => {
      if (other !== video && !other.muted) this.#setSound(other, false);
    });
    this.#setSound(video, turnOn);
    if (turnOn) {
      video.play().catch(() => {});
    } else if (this.#reduceMotion.matches) {
      video.pause();
    }
  }
}

if (!customElements.get('s-reaction-reel')) customElements.define('s-reaction-reel', SReactionReel);
